import { Icon } from "@pepperstone/pepper-ui-kit";

import { useBodyScrollLock, useIsMobile } from "../hooks";
import { Typo } from "../Typo";

export type ConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  cancelLabel: string;
  okLabel: string;
  onCancel: () => void;
  onSubmit: () => void;
};

export const ConfirmationModal = ({
  isOpen,
  onClose,
  title,
  description,
  cancelLabel,
  okLabel,
  onCancel,
  onSubmit,
}: ConfirmationModalProps) => {
  const isMobile = useIsMobile();
  useBodyScrollLock(isOpen);
  if (!isOpen) return null;

  return (
    <div
      data-testid="confirmation-modal"
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div
        className="absolute inset-0 animate-modal-fade-in bg-black/50"
        onClick={onClose}
      />

      <div className="relative z-10 mx-4 w-[328px] max-w-md animate-modal-scale-in rounded-2xl bg-white px-6 py-4 shadow-xl lg:mx-0 lg:min-w-[512px] lg:p-8">
        <div className="flex shrink-0 justify-end">
          <button
            data-testid="confirmation-modal-close-button"
            onClick={onClose}
            className="flex h-6 w-6 items-center justify-center"
          >
            <Icon icon="cross" size="small" color="black" aria-label="Close" />
          </button>
        </div>

        <div className="mt-2 flex flex-col gap-4 lg:gap-6">
          <Typo element="h5" smType="headingMD" lgType="headingXL">
            {title}
          </Typo>
          <Typo
            type={isMobile ? "bodySM" : "bodyMD"}
            textColor="text-text-secondary"
          >
            {description}
          </Typo>

          <div className="mt-2 flex flex-col-reverse justify-end gap-3 lg:flex-row">
            <button
              data-testid="confirmation-modal-cancel-button"
              type="button"
              onClick={onCancel}
              className="flex h-10 cursor-pointer items-center justify-center rounded-lg bg-transparent lg:min-w-20 lg:px-3 lg:py-2"
            >
              <Typo type="labelSM" textColor="text-text-primary">
                {cancelLabel}
              </Typo>
            </button>
            <button
              data-testid="confirmation-modal-confirm-button"
              onClick={onSubmit}
              className="flex h-10 cursor-pointer items-center justify-center rounded-lg bg-surface-brand-default lg:min-w-20 lg:px-3 lg:py-2"
            >
              <Typo type="labelSM" textColor="text-text-inverse-primary">
                {okLabel}
              </Typo>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
